import { useState, useEffect, useCallback } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Package,
  ShoppingCart,
  Loader2,
  Download,
  MapPin,
  Calendar,
} from 'lucide-react';
import type { PhotoResult, PackInfo, DownloadLink } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../lib/api';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface GalleryModalProps {
  /** If opening a pack, pass pack info + we fetch its photos */
  pack?: PackInfo | null;
  /** If opening an individual photo, pass the photo + siblings */
  photos?: PhotoResult[];
  /** Index to start at within photos array */
  startIndex?: number;
  /** Close the modal */
  onClose: () => void;
  /** Called after a successful purchase */
  onPurchaseComplete?: (links: DownloadLink[]) => void;
  /** Show the auth form */
  onRequireAuth?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function PhotoGalleryModal({
  pack,
  photos: externalPhotos,
  startIndex = 0,
  onClose,
  onPurchaseComplete,
  onRequireAuth,
}: GalleryModalProps) {
  const { user, token } = useAuth();

  const [photos, setPhotos] = useState<PhotoResult[]>(externalPhotos ?? []);
  const [packInfo, setPackInfo] = useState<PackInfo | null>(pack ?? null);
  const [loading, setLoading] = useState(!!pack && !externalPhotos?.length);
  const [current, setCurrent] = useState(startIndex);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [downloadLinks, setDownloadLinks] = useState<DownloadLink[] | null>(null);

  // Fetch pack photos if pack was passed without photos
  useEffect(() => {
    if (pack && (!externalPhotos || externalPhotos.length === 0)) {
      setLoading(true);
      fetch(apiUrl(`/api/photos/pack/${pack._id}/photos`))
        .then(res => res.json())
        .then(data => {
          setPhotos(data.photos ?? []);
          if (data.pack) {
            setPackInfo({
              _id: data.pack._id?.toString() ?? pack._id,
              name: data.pack.name,
              price: data.pack.price,
              mediaCount: data.pack.mediaCount,
              beach: data.pack.beach,
              date: data.pack.date,
            });
          }
        })
        .catch(() => setPhotos([]))
        .finally(() => setLoading(false));
    }
  }, [pack, externalPhotos]);

  // Keyboard navigation
  const goNext = useCallback(() => {
    setCurrent(prev => (prev < photos.length - 1 ? prev + 1 : prev));
  }, [photos.length]);

  const goPrev = useCallback(() => {
    setCurrent(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, goNext, goPrev]);

  // Toggle selection
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(photos.map(p => p._id)));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Compute selected total
  const selectedTotal = photos
    .filter(p => selectedIds.has(p._id))
    .reduce((sum, p) => sum + p.price, 0);

  // Purchase
  const doPurchase = async (payload: { photoIds?: string[]; packId?: string }) => {
    if (!user || !token) {
      onRequireAuth?.();
      return;
    }
    setPurchasing(true);
    setPurchaseError('');
    try {
      const res = await fetch(apiUrl('/api/photos/purchase'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Purchase failed');
      setDownloadLinks(data.downloadUrls);
      setSelectedIds(new Set());
      onPurchaseComplete?.(data.downloadUrls);
    } catch (err: unknown) {
      setPurchaseError(err instanceof Error ? err.message : 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const buySelected = () => {
    if (selectedIds.size === 0) return;
    doPurchase({ photoIds: Array.from(selectedIds) });
  };

  const buyPack = () => {
    if (!packInfo) return;
    doPurchase({ packId: packInfo._id });
  };

  const currentPhoto = photos[current];

  /* ---------------------------------------------------------------- */
  /*  Render: download links view                                      */
  /* ---------------------------------------------------------------- */
  if (downloadLinks) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative max-h-[85vh] overflow-y-auto mx-4"
          onClick={e => e.stopPropagation()}
        >
          <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-bold mb-2">Purchase Complete</h2>
          <p className="text-slate-500 text-sm mb-6">
            Your unwatermarked, full-resolution files are ready. Links expire in 1 hour.
          </p>
          <div className="space-y-3">
            {downloadLinks.map(link => (
              <a
                key={link.photoId}
                href={link.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Download className="h-5 w-5 text-blue-600 shrink-0" />
                <span className="truncate text-sm font-medium">{link.filename}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Render: main gallery                                             */
  /* ---------------------------------------------------------------- */
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 sm:px-6 py-3 bg-black/60 text-white shrink-0"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 min-w-0">
          {packInfo && (
            <span className="flex items-center gap-1.5 text-sm font-medium bg-white/15 px-3 py-1 rounded-full truncate">
              <Package className="h-3.5 w-3.5 shrink-0" />
              {packInfo.name}
              <span className="text-white/60 ml-1">({packInfo.mediaCount} photos)</span>
            </span>
          )}
          {!loading && photos.length > 0 && (
            <span className="text-sm text-white/60">
              {current + 1} / {photos.length}
            </span>
          )}
        </div>
        <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex relative overflow-hidden min-h-0" onClick={e => e.stopPropagation()}>
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-10 w-10 text-white animate-spin" />
          </div>
        ) : photos.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-white/50">
            <p>No photos found</p>
          </div>
        ) : (
          <>
            {/* Left arrow */}
            {current > 0 && (
              <button
                onClick={goPrev}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 p-2 sm:p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
              >
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            )}

            {/* Photo display */}
            <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
              {currentPhoto && (
                <div className="relative max-w-full max-h-full flex flex-col items-center">
                  {currentPhoto.mediaType === 'image' ? (
                    <img
                      src={currentPhoto.watermarkedUrl}
                      alt={currentPhoto.filename}
                      className="max-h-[calc(100vh-240px)] max-w-full object-contain rounded-lg shadow-2xl"
                      draggable={false}
                    />
                  ) : (
                    <video
                      src={currentPhoto.watermarkedUrl}
                      className="max-h-[calc(100vh-240px)] max-w-full object-contain rounded-lg shadow-2xl"
                      controls
                      playsInline
                    />
                  )}
                </div>
              )}
            </div>

            {/* Right arrow */}
            {current < photos.length - 1 && (
              <button
                onClick={goNext}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 p-2 sm:p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
              >
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Bottom bar: thumbnail strip + purchase controls */}
      {!loading && photos.length > 0 && (
        <div
          className="bg-black/70 border-t border-white/10 shrink-0"
          onClick={e => e.stopPropagation()}
        >
          {/* Thumbnail strip */}
          <div className="px-4 sm:px-6 py-3 overflow-x-auto">
            <div className="flex gap-2 justify-start min-w-min">
              {photos.map((photo, idx) => {
                const isActive = idx === current;
                const isSelected = selectedIds.has(photo._id);
                return (
                  <button
                    key={photo._id}
                    onClick={() => setCurrent(idx)}
                    className={`relative shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      isActive
                        ? 'border-white ring-1 ring-white/40'
                        : isSelected
                          ? 'border-blue-500'
                          : 'border-transparent hover:border-white/30'
                    }`}
                  >
                    <img
                      src={photo.watermarkedUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      draggable={false}
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-blue-600/30 flex items-center justify-center">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Purchase error */}
          {purchaseError && (
            <div className="mx-4 sm:mx-6 mb-2 px-3 py-2 bg-red-500/20 text-red-200 rounded-lg text-sm flex items-center justify-between">
              <span>{purchaseError}</span>
              <button onClick={() => setPurchaseError('')} className="text-red-300 hover:text-white">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Action bar */}
          <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap border-t border-white/5">
            {/* Photo info */}
            {currentPhoto && (
              <div className="text-white/70 text-sm flex items-center gap-3 flex-wrap min-w-0">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {currentPhoto.beach}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(currentPhoto.date).toLocaleDateString()}
                </span>
                <span className="font-semibold text-white">${currentPhoto.price.toFixed(2)}</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Toggle current photo selection */}
              {currentPhoto && (
                <button
                  onClick={() => toggleSelect(currentPhoto._id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedIds.has(currentPhoto._id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {selectedIds.has(currentPhoto._id) ? 'Selected' : 'Select'}
                </button>
              )}

              {/* Select all / clear */}
              {photos.length > 1 && (
                <button
                  onClick={selectedIds.size === photos.length ? clearSelection : selectAll}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  {selectedIds.size === photos.length ? 'Clear All' : 'Select All'}
                </button>
              )}

              {/* Buy selected */}
              {selectedIds.size > 0 && (
                <button
                  onClick={buySelected}
                  disabled={purchasing}
                  className="px-4 py-1.5 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                >
                  {purchasing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShoppingCart className="h-3.5 w-3.5" />}
                  Buy {selectedIds.size} (${selectedTotal.toFixed(2)})
                </button>
              )}

              {/* Buy pack */}
              {packInfo && packInfo.price > 0 && (
                <button
                  onClick={buyPack}
                  disabled={purchasing}
                  className="px-4 py-1.5 rounded-lg text-sm font-bold bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                >
                  {purchasing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  <Package className="h-3.5 w-3.5" />
                  Buy Pack (${packInfo.price.toFixed(2)})
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
